import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, IconButton } from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import { useTheme } from '@mui/system';
import MainCard from 'components/Cards/MainCard';
import { API_URL, gridSpacing } from 'utils/constants';
import AddPolicyDialog from 'containers/Management/Policies/AddPolicyDialog';
import ApiHelper from 'utils/apiHelper';
import { toast } from 'react-toastify';
import EditPolicyDialog from './EditPolicyDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';

const ManagementPolicies = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [showAddPolicyDialog, setShowAddPolicyDialog] = useState(false);
    const [showEditPolicyDialog, setShowEditPolicyDialog] = useState(false);
    const [policiesData, setPoliciesData] = useState([]);
    const [selectedPolicyData, setSelectedPolicyData] = useState('');
    const [deletePolicyId, setDeletePolicyId] = useState();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [striptStatus, setStriptStatus] = useState(false);
    const [backgrundScheme, setBackgroundScheme] = useState(false);
    const [timeIntervals, setTimeIntervals] = useState([]);
    const [inputFields, setInputFields] = useState({});
    const [schemeinputFields, setSchemeinputFields] = useState({});

    const getTimeIntervals = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'time_intervals' }).then((res) => {
            setTimeIntervals(res.data['timeIntervals']);
        });
    };

    const getPoliciesData = async () => {
        const res = await ApiHelper.get(API_URL.MANAGEMENT_POLICY);
        if (res.data) {
            setPoliciesData(res.data.policy_data);
            setBackgroundScheme(res.data.background_scheme);
            setLoading(false);
        } else {
            toast.error('Failed to get the policy data.');
            setLoading(false);
        }
    };

    const getScriptStatus = () => {
        ApiHelper.get('/api/server/script_status/')
            .then((res) => {
                setStriptStatus(res.data);
            })
            .catch((err) => {
                toast.error('Failed to get the script status.');
            });
    };
    useEffect(() => {
        getScriptStatus();
        getPoliciesData();
        getTimeIntervals();
    }, []);

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeletePolicyId(id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleDelete = () => {
        ApiHelper.delete(`/api/management/policy/${deletePolicyId}/`)
            .then(() => {
                toast.success('Successfully deleted the policy.');
                getPoliciesData();
                handleClose();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
                handleClose();
            });
    };

    const handleAddPolicy = () => {
        setShowAddPolicyDialog(false);
        getPoliciesData();
    };

    const handleCloseEditPolicyDialog = () => {
        setShowEditPolicyDialog(false);
        getPoliciesData();
    };

    const handleEditPolicy = (data) => {
        if (data.locked === 1) {
            toast.warning('This policy was locked, so you can not edit it.');
            return true;
        }
        localStorage.removeItem('policySchemeData');
        localStorage.removeItem('policyScheduledEventsData');
        localStorage.setItem('policySchemeData', JSON.stringify(data.schemes));
        localStorage.setItem('policyScheduledEventsData', JSON.stringify(data.scheduled_events));
        setShowEditPolicyDialog(true);
        setSelectedPolicyData(data);
    };

    if (!policiesData) {
        return null;
    } else {
        return (
            <MainCard title="Policy Management" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Policies</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowAddPolicyDialog(true)}
                                    startIcon={<Add />}
                                >
                                    Add Policy
                                </Button>
                                {showAddPolicyDialog && (
                                    <AddPolicyDialog
                                        onClose={handleAddPolicy}
                                        scriptStatus={striptStatus}
                                        inputFields={inputFields}
                                        schemeInputFields={schemeinputFields}
                                        backgroundScheme={backgrundScheme}
                                        timeIntervals={timeIntervals}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={gridSpacing}>
                            <Grid item lg={12} xs={12}>
                                {loading === true ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                        Please wait...
                                    </Typography>
                                ) : policiesData.length === 0 ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center' }}>No Policies Data</Typography>
                                ) : (
                                    <Table sx={{ minWidth: 700 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Policy Name</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell>Locked</TableCell>
                                                <TableCell align="right">Options</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {policiesData.map((data, key) => (
                                                <TableRow key={key}>
                                                    <TableCell>{data.name}</TableCell>
                                                    <TableCell>{data.description}</TableCell>
                                                    <TableCell>{data.locked === 1 ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell align="right">
                                                        <Box>
                                                            {!data.locked && (
                                                                <IconButton size="small" onClick={() => handleEditPolicy(data)}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            {!data.locked && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteConfirmation(data.policyid)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {showEditPolicyDialog && (
                                                <EditPolicyDialog
                                                    onClose={handleCloseEditPolicyDialog}
                                                    policyData={selectedPolicyData}
                                                    scriptStatus={striptStatus}
                                                    inputFields={inputFields}
                                                    schemeinputFields={schemeinputFields}
                                                    timeIntervals={timeIntervals}
                                                />
                                            )}
                                            {showDeleteConfirmationDialog && (
                                                <DeleteConfirmationDialog onClose={handleClose} onYes={handleDelete} name="policy" />
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementPolicies;
